<template>
    <div>
        <div class="p-4 rounded-lg shadow-sm bg-white mb-2">
            <h4 class="font-weight-bold">Credit Card Expense</h4>

            <div class="float-right">
                Currency:
                <select v-model="expense.currency" :disabled="wait">
                    <option>KES</option>
                    <option>USD</option>
                </select>
            </div>
            <p>
                Credit Card Expense Allocation Form: <br>
                Integrating and mapping transactions to individual budget lines for the selected month
            </p>
            <p v-if="expense.id">
                ERP Code: <b>{{ expense.code }}</b> -
                <span class="text-muted">{{ day(expense.created_at).format('MMM, DD YYYY H:mm A') }}</span> .
            </p>

            <Alerts :_success="success" :_error="error"></Alerts>

            <div class="form-group">
                <label class="font-weight-bold">Enter Title</label>
                <textarea class="form-control" rows="1" required v-model="expense.title" :disabled="wait|| !allowEdit()"
                          placeholder="Enter title, (eg Credit Card Expense Month of x)"></textarea>
            </div>

            <form>
                <p v-if="!expense.id" class="text-muted">
                    Enter transaction items and select associated budget line
                </p>

                <div v-if="expense.status!=='pending'" class="alert border rounded-lg p-2 text-success border-success">
                    Credit card expense status is <span class="status" :class="expense.status">{{
                        expense.status
                    }}</span> <br>
                    You cannot make any further adjustments
                </div>

                <div v-if="passed !== null" class="alert mb-3" :class="passed ? 'alert-success' : 'alert-danger'">
                    <h6 class="font-weight-bold">
                        Credit card transactions {{ passed ? 'passed' : 'failed' }}
                    </h6>
                    <template v-if="passed">
                        Your credit card transactions have passed the validation check.
                        Please click on <b>"Save"</b> to submit the budgeting
                    </template>
                    <template v-else>
                        Sorry, validation check failed.
                        Please review them and make the necessary adjustments
                    </template>
                </div>

                <table class="table table-sm table-bordered mb-3">
                    <thead class="bg-secondary text-white">
                    <tr>
                        <th>SN</th>
                        <th style="width:40%">ITEM</th>
                        <th></th>
                        <th>BUDGET</th>
                        <th class="text-right">AMOUNT</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(i, index) in expense.budgeting" :key="index">
                        <td>{{ index + 1 }}</td>
                        <td>
                            <input class="form-control" :disabled="wait || !allowEdit()"
                                   :class="i.error ? 'border-danger': (i.success ? 'border-success' : '')"
                                   v-model="i.item"
                                   @keyup="resetValidation(i)"
                                   rows="1" placeholder="Enter item" required>
                            <small v-if="i.error && i.message" class="text-danger">{{ i.message }}</small>
                        </td>
                        <td><span :title="i.status" class="status pr-1" :class="i.status"></span></td>
                        <td>
                            <select class="form-control" :disabled="wait || !allowEdit()"
                                    :class="i.error ? 'border-danger': (i.success ? 'border-success' : '')"
                                    @change="handleBudgetChange(i)"
                                    v-model="i.budget_id" required>
                                <option value="">--Select budget line--</option>
                                <optgroup v-for="(records, cat) in budgets" :label="cat" :key="cat">
                                    <option v-for="record in records" :value="record.id" :key="record.id">{{
                                            record.name
                                        }}
                                    </option>
                                </optgroup>
                            </select>
                        </td>

                        <td class="text-right">
                            <input v-model="i.amount" class="form-control text-right"
                                   :disabled="wait || !allowEdit() || !i.item"
                                   :class="i.error ? 'border-danger': (i.success ? 'border-success' : '')"
                                   @keyup="resetValidation(i)"
                                   placeholder="Enter amount" required>
                        </td>
                        <td>
                            <a v-if="expense.status ==='pending'" href="#" class="btn btn-danger btn-sm"
                               @click="deleteRow(index)">X
                            </a>
                        </td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                        <th></th>
                        <th colspan="3">TOTALS</th>
                        <th class="text-right">{{ sum() }}</th>
                    </tr>
                    </tfoot>
                </table>

                <div v-if="allowEdit()">
                    <button class="btn btn-outline-dark" @click.prevent="addRow()" :disabled="wait">Add Row</button>&nbsp;

                    <div class="float-right">
                        <button v-if="!expense.id && expense.budgeting.length" :disabled="wait" @click="resetExpense()" class="btn btn-danger my-2">
                            Reset Form
                        </button>&nbsp;

                        <button class="btn btn-success" type="submit" @click.prevent="submitRows()"
                                :disabled="wait || !expense.budgeting.length">
                            <span v-if="wait" class="spinner-border spinner-border-sm"></span>&nbsp;

                            <template v-if="passed && passed===true">Save</template>
                            <template v-else>Validate</template>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import Alerts from "./Alerts";

export default {
    name: "CreditCard",
    components: {Alerts},
    props: ['code'],
    data() {
        return {
            axios: this.$http,
            base_url: "",

            create_new: false,
            passed: null,

            expense: {
                title: '',
                currency: 'USD',
                budgeting: [],
                status: 'pending',
            },

            row: {
                item: '',
                budget_id: '',
                status: 'pending',
                amount: 0
            },

            budgets: [],
            months: [
                "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"
            ]
        }
    },
    mounted() {
        // Load budgets
        this.axios(this.base_url + '/budgets').then(res => {
            this.budgets = res.data.data
        })

        this.setCurrentMonth()

        // Load from localStorage if available
        const fromStorage = localStorage.getItem('expense-budgeting');
        if (fromStorage) {
            this.expense.budgeting = JSON.parse(fromStorage);
        } else {
            this.addRow();
        }

        // Load expense
        if (this.code) {
            this.axios(this.base_url + '/card/get/' + this.code).then(res => {
                this.expense = res.data.data
                this.expense.budgeting = JSON.parse(res.data.data.budgeting)
            })
        }

    },

    methods: {
        handleBudgetChange(budget) {
            this.resetValidation(budget)
            this.updateLocalStorage()
        },
        allowEdit() {
            return (this.expense.status === 'pending')
        },
        resetExpense() {
            if (confirm('Are you sure you want to start new work sheet?')) {
                this.expense = {
                    title: '',
                    status: 'pending',
                    currency: 'USD',
                    budgeting: []
                }
                this.create_new = true
                this.setCurrentMonth()
                this.updateLocalStorage()
            }
        },
        resetValidation(item) {
            item.success = item.error = ''
            this.success = this.error = ''
            this.resetPassed()
        },
        updateLocalStorage() {
            localStorage.setItem('expense-budgeting', JSON.stringify(this.expense.budgeting));
        },
        clearLocalStorage() {
            localStorage.removeItem('expense-budgeting');
        },
        resetPassed() {
            if (this.passed && this.passed == true) {
                this.passed = null
            }
        },
        addRow() {
            this.expense.budgeting.push({...this.row})
            this.resetPassed()
            this.updateLocalStorage()
        },
        deleteRow(index) {
            this.expense.budgeting.splice(index, 1);
            this.resetPassed()
            this.updateLocalStorage()
        },
        sum() {
            let total = this.expense.budgeting.reduce((sum, row) => {
                return sum += parseFloat(row.amount);
            }, 0);
            return !isNaN(total) ? total : 0
        },

        setCurrentMonth() {
            const month = this.months[new Date().getMonth()]
            this.expense.title = `To pay credit card month of ${month}`
        },

        submitRows() {
            this.clear()
            this.wait = true
            this.success = 'Please wait...'

            if (this.passed) {
                this.axios.post(this.base_url + '/card/save', this.expense).then(res => {
                    let code = res.data.data.code
                    this.success = "Card details saved successfully. Erp code " + code
                    this.clearLocalStorage()
                    this.$router.push(`/requests/${code}`)

                }).catch((err) => {
                    this.showError(err)
                }).then(() => {
                    this.success = ''
                    this.wait = false
                })

            } else {
                this.axios.post(this.base_url + '/card/review', this.expense).then(res => {
                    this.expense.budgeting = res.data.data.budgeting
                    this.passed = res.data.data.passed
                    this.passed ? this.success = 'Validation check passed' : this.error = 'Validation check failed'
                    this.updateLocalStorage()

                }).catch(err => {
                    this.showError(err)
                }).then(() => {
                    this.success = ''
                    this.wait = false
                })
            }
        },
    }
}
</script>

<style scoped>

</style>